import type { ComponentProps } from 'react'
import { TooltipShortcutHint } from '@/components/layout/KeyboardShortcutsDialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { FOCUS_RING } from '@/lib/focus-ring'
import { cn } from '@/lib/utils'
import { useBennyStore } from '@/store/benny.store'

import { useBennyAvailable } from './useBennyAvailable'
import { useOpenBenny } from './useOpenBenny'

import './ask-benny-pill.css'

/**
 * The Ask Benny pill itself: Benny's face, the label and the gradient ring.
 * Presentational so every Benny door looks identical — the org top bar wraps
 * it in its own open logic, the client portal wraps it in a popover trigger.
 * Trigger wrappers (Radix `asChild`) override `data-state`/`aria-expanded`.
 */
export function AskBennyPill({
  open = false,
  active = false,
  className,
  labelClassName,
  ref,
  ...props
}: ComponentProps<'button'> & {
  open?: boolean
  /** Benny is showing (pinned or popover): draw the pill pressed. */
  active?: boolean
  /** Extra classes for the visible label, e.g. to hide it on narrow bars. */
  labelClassName?: string
}) {
  return (
    <button
      ref={ref}
      type="button"
      data-benny-launcher
      data-benny-button
      data-state={open ? 'open' : 'closed'}
      data-benny-active={active ? 'true' : undefined}
      aria-label="Ask Benny"
      aria-haspopup="dialog"
      aria-expanded={open}
      {...props}
      className={cn(
        // Top-bar chip scale: 32px tall, 13px label (--font-size-sm).
        'ask-benny-pill group relative flex h-8 shrink-0 items-center rounded-full text-[length:var(--font-size-sm)]',
        'transition-transform duration-200 hover:scale-[1.03] active:scale-[0.97] motion-reduce:transition-none',
        FOCUS_RING,
        className,
      )}
    >
      <span className="ask-benny-pill__inner flex h-full items-center gap-2 rounded-full pl-1 pr-2">
        <span
          aria-hidden
          className="bg-purple-hen flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full"
          style={{
            backgroundImage: 'url(/characters/Default.svg)',
            backgroundSize: '140%',
            backgroundPosition: 'center 10%',
            backgroundRepeat: 'no-repeat',
          }}
        />
        <span
          className={cn(
            'ask-benny-pill__label pr-0.5 text-[length:var(--font-size-sm)] font-medium leading-none tracking-tight',
            labelClassName,
          )}
        >
          Ask Benny
        </span>
      </span>
    </button>
  )
}

/**
 * The one Benny door in the top bar, on web and in the desktop title bar.
 *
 * Opens the popover under itself (page-aware, via useOpenBenny); the popover's
 * own Expand control hands the thread to the Conversations page. Renders
 * nothing only when Benny is off for this user; it stays in the desktop
 * title bar even while the companion pet is running.
 */
export function AskBennyButton({
  className,
  labelClassName,
}: {
  className?: string
  /** Extra classes for the visible label, e.g. to hide it on narrow bars. */
  labelClassName?: string
}) {
  const available = useBennyAvailable()
  const open = useBennyStore(state => state.open)
  const isPinned = useBennyStore(state => state.mode === 'docked')
  const setOpen = useBennyStore(state => state.setOpen)
  const openBenny = useOpenBenny()

  if (!available) return null

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <AskBennyPill
            open={open}
            // Pressed while Benny shows; a second click hides him (a pinned
            // pane reopens pinned next time, the preference is kept).
            active={open}
            aria-pressed={open}
            className={className}
            labelClassName={labelClassName}
            onClick={() => {
              if (open) setOpen(false)
              else void openBenny()
            }}
          />
        </TooltipTrigger>
        <TooltipContent side="bottom" sideOffset={6}>
          {open
            ? isPinned
              ? 'Hide Benny (stays pinned for next time)'
              : 'Close Benny'
            : 'Ask Benny about this page'}
          <TooltipShortcutHint keys={['Mod', '.']} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
